import goTopButton from '@/components/GoTopButton'
import ProductErrorDialog from '@/components/ProductErrorDialog'
import SuccessDialogProduct from '@/components/SuccessDialogProduct'
import { defineRule, Form, Field, ErrorMessage, configure } from 'vee-validate';
import { required, regex } from '@vee-validate/rules';
import { setLocale, localize } from "@vee-validate/i18n";
import { exportProduct, getPlus1Date } from '@/tools/utils.js'
import { useToast } from 'vue-toast-notification'
import isEqual from 'lodash/isEqual'
import { Modal } from 'bootstrap';

defineRule('required', required);
defineRule('regex', regex);

configure({
  generateMessage: localize({
    fr: {
      messages: {
        required: 'Le champ {field} est obligatoire',
        regex: 'Le format du champ {field} n\'est pas respecté'
      },
    },
    en: {
      messages: {
        required: 'The field {field} is required',
        regex: 'The format of the field {field} is not respected'
      },
    },
  }),
});

const messages = {
  fr: {
    required: 'Le champ {field} est obligatoire',
    regex: 'Le format du champ {field} n\'est pas respecté',
  },
  en: {
    required: 'The field {field} is required',
    regex: 'The format of the field {field} is not respected',
  }
};

function getLocalizedMessage(field, rule, locale) {
  const messageTemplate = messages[locale][rule];
  return messageTemplate.replace('{field}', field);
}

export default {

  components: {
    goTopButton,
    ProductErrorDialog,
    SuccessDialogProduct,
    Form,
    Field,
    ErrorMessage
  },

  data () {
    return {
      permissions: JSON.parse(sessionStorage.getItem('user')).permissions,
      responseErr: null,
      modalError: null,
      isProcessing: false,
      locale: null,
      product: {},
      productErrors: {},
      productStructure: null,
      distributors: [],
      selectedDistributor: null,
      isDistributorLocked: true,
      selectedDistributorRef: '',
      updateDate: null,
      dateError: false,
      createdProductsLastMonth: null,
      createdProductsThisMonth: null,
      jsonPastedObject: null,
      readingMode : {
        'MANUAL' : 'O',
        'CHIP' : 'O',
        'CHIP_CONTACTLESS' : 'O',
        'MAGSTRIPE' : 'N',
        'MAGSTRIPE_CONTACTLESS' : 'N',
        'MANUAL_MAGSTRIPE_FALLBACK' : 'N',
        'CHIP_TO_MAGSTRIPE_FALLBACK' : 'N',
        'SERVER' : 'O',
        'UNKNOWN' : 'N'
      },
      blockErrors: {
        'PRB': false,
        'PRE': false,
        'PRC': false,
        'PRS': false,
        'PRO': false,
        'PRF': false
      },
      keyValues: [
        'KPV',
        'KCVx',
        'KEMV'
      ]
    }
  },

  created () {
    const user = JSON.parse(sessionStorage.getItem('user'))
    // set locale for vee-validate error messages
    this.setVeeValidateLocale(user)
    if (user.ihmDistribRef) {
      this.criterias.selectedDistributorRef = user.ihmDistribRef
      this.isDistributorLocked = true
    } else {
      this.isDistributorLocked = false
      this.getDistributors(user)
    }

    // Retrieve number of created products for the current month and the previous month
    this.$store.dispatch('products/createdProductsLastMonth').then(
      (response) => {
        if(response.responseCode != 'SUCCESS') {
          this.createdProductsLastMonth = null
        }
        else {
          this.createdProductsLastMonth = response.numberOfProducts
        }
      },
      error => {
        this.responseErr = error || 'BAD_REQUEST'
      }
    )
    this.$store.dispatch('products/createdProductsCurrentMonth').then(
      (response) => {
        if(response.responseCode != 'SUCCESS') {
          this.createdProductsThisMonth = null
        }
        else {
          this.createdProductsThisMonth = response.numberOfProducts
        }
      },
      error => {
        this.responseErr = error || 'BAD_REQUEST'
      }
    )

    this.$store.dispatch('products/resetViolations')
    this.getStructure()
    this.updateDate = getPlus1Date()
    this.getDistributorsDetail()

    if (this.$store.state.distributors.distributor) {
      this.selectedDistributorRef = this.$store.state.distributors.distributor.reference
    }
  },

  methods: {
    // Method to initialize the watchers for the product fields
    // Also initialize a watcher for PRB-7 to update the distributor and get wanted values
    // PRE-3 / PRE-4 / PRE-5 and PRS-25 / PRS-26 / PRS-27 have the same values
    // PRE-21 must be one the values of PRS-29
    // PRS-32 / 33 / 34 are updated accordingly to PRS-31 / 28 / 25 / 26 / 27
    initWatchers () {
      this.$watch('selectedDistributorRef', (newValue) => {
        if(this.selectedDistributor == null || this.selectedDistributor.reference !== newValue) {
          for(const distributor of this.$store.state.distributors.distributors) {
            if(distributor.reference === newValue) {
              this.selectedDistributor = distributor
              // Change the prb11 value to the same value if the distributor is changed to trigger the pre28 watcher
              let tmpSchemes = this.product['product']['prb']['prb11']
              this.product['product']['prb']['prb11'] = ''
              this.product['product']['prb']['prb11'] = tmpSchemes
              break
            }
          }
        }
      }, {
        deep: true
      });
      this.$watch('product.product.prb.prb11', (newValue) => {
        let issuerIdString = ''
        let indexIssuerId = '??'
        if (this.selectedDistributor) {
          issuerIdString = this.selectedDistributor.issuerId.toString()
          indexIssuerId = issuerIdString[1] + issuerIdString[2]
        }
        this.product['product']['pre']['pre28'] = ''
        const schemes = newValue ? newValue.split(/\s+/) : [];
        for (const scheme of schemes) {
          for (const key of this.keyValues) {
            this.product['product']['pre']['pre28'] += scheme
              + '/' + key + '/'
            if(key === 'KEMV') {
              if(scheme === 'MASTERCARD' || scheme === 'PRIV' || scheme === 'CB') {
                this.product['product']['pre']['pre28'] += '0X' + '\n'
              }
              else {
                // If an other scheme than MASTERCARD, PRIV or CB is selected, we indicate an error
                this.product['product']['pre']['pre28'] = 'INVALID_PRB-11'
              }
            }
            else {
              this.product['product']['pre']['pre28'] += 'X' + '/'
              if (key === 'KPV') {
                if(scheme === 'MASTERCARD' || scheme === 'PRIV' || scheme === 'CB') {
                  this.product['product']['pre']['pre28'] += 'KPVUMU' + indexIssuerId
                    + '/' + '[SECU/ISECU]' + '\n'
                }
                else {
                  // If an other scheme than MASTERCARD, PRIV or CB is selected, we indicate an error
                  this.product['product']['pre']['pre28'] = 'INVALID_PRB-11'
                }
              }
              else if (key === 'KCVx') {
                if(scheme === 'MASTERCARD') {
                  this.product['product']['pre']['pre28'] += 'CVCUMU' + indexIssuerId
                    + '/' + '[SECU/ISECU]' + '\n'
                }
                else if (scheme === 'PRIV' || scheme === 'CB') {
                  this.product['product']['pre']['pre28'] += 'CVVUMU' + indexIssuerId
                    + '/' + '[SECU/ISECU]' + '\n'
                }
                else {
                  // If an other scheme than MASTERCARD, PRIV or CB is selected, we indicate an error
                  this.product['product']['pre']['pre28'] = 'INVALID_PRB-11'
                }
              }
            }
          }
        }
        this.updateErrors('pre', 'PRE-28', newValue, this.productStructure[1].params[25])
      }, {
        deep: true
      });
      this.$watch('product.product.pre.pre3', (newValue) => {
        this.product['product']['prs']['prs25'] = newValue
        this.updateErrors('prs', 'PRS-25', newValue, this.productStructure[3].params[24])
      }, {
        deep: true
      });
      this.$watch('product.product.prs.prs25', (newValue) => {
        this.product['product']['pre']['pre3'] = newValue
        this.updateErrors('pre', 'PRE-3', newValue, this.productStructure[1].params[1])
      }, {
        deep: true
      });
      this.$watch('product.product.pre.pre4', (newValue) => {
        this.product['product']['prs']['prs26'] = newValue
        this.updateErrors('prs', 'PRS-26', newValue, this.productStructure[3].params[25])
      }, {
        deep: true
      });
      this.$watch('product.product.prs.prs26', (newValue) => {
        this.product['product']['pre']['pre4'] = newValue
        this.updateErrors('pre', 'PRE-4', newValue, this.productStructure[1].params[2])
      }, {
        deep: true
      });
      this.$watch('product.product.pre.pre5', (newValue) => {
        this.product['product']['prs']['prs27'] = newValue
        this.updateErrors('prs', 'PRS-27', newValue, this.productStructure[3].params[26])
      }, {
        deep: true
      });
      this.$watch('product.product.prs.prs27', (newValue) => {
        this.product['product']['pre']['pre5'] = newValue
        this.updateErrors('pre', 'PRE-5', newValue, this.productStructure[1].params[3])
      }, {
        deep: true
      });
      this.$watch('product.product.pre.pre21', (newValue) => {
        this.product['product']['prs']['prs29'] = newValue
        this.updateErrors('prs', 'PRS-29', newValue, this.productStructure[3].params[28])
      }, {
        deep: true
      });
      // Watchers for PRS-32 / 33 / 34
      // If PRS-31 is updated, PRS-32 / 33 / 34 are updated accordingly
      this.$watch('product.product.prs.prs31', (newValue) => {
        this.product['product']['prs']['prs32'] = '00' + ' / ' + newValue + ' / '
          + this.product['product']['prs']['prs28'] + ' / '
          + this.product['product']['prs']['prs25'] + ' / '
          + this.product['product']['prs']['prs26'] + ' / '
          + this.product['product']['prs']['prs27'] + ' / ' + '00\n'
        + '10' + ' / ' + newValue + ' / '
          + this.product['product']['prs']['prs28'] + ' / '
          + this.product['product']['prs']['prs25'] + ' / '
          + this.product['product']['prs']['prs26'] + ' / '
          + this.product['product']['prs']['prs27'] + ' / ' + '05'
        this.product['product']['prs']['prs34'] = newValue
        this.updateErrors('prs', 'PRS-32', newValue, this.productStructure[3].params[31])
        this.product['product']['prs']['prs33'] = ''
        Object.entries(this.readingMode).forEach(([key, value]) => {
          this.product['product']['prs']['prs33'] += newValue + ' / '
          + this.product['product']['prs']['prs28'] + ' / '
          + this.product['product']['prs']['prs25'] + ' / '
          + this.product['product']['prs']['prs26'] + ' / '
          + this.product['product']['prs']['prs27'] + ' / '
          + key + ' / ' + value
          if(key !== 'UNKNOWN') {
            this.product['product']['prs']['prs33'] += '\n'
          }
        })
        this.updateErrors('prs', 'PRS-33', newValue, this.productStructure[3].params[32])
        this.product['product']['prs']['prs34'] = newValue + ' / '
        + this.product['product']['prs']['prs28'] + ' / '
        + this.product['product']['prs']['prs25'] + ' / '
        + this.product['product']['prs']['prs26'] + ' / '
        + this.product['product']['prs']['prs27'] + ' / ' + 'R'
        this.updateErrors('prs', 'PRS-34', newValue, this.productStructure[3].params[33])
      }, {
        deep: true
      });
      // If PRS-28 is updated, PRS-32 / 33 / 34 are updated accordingly
      this.$watch('product.product.prs.prs28', (newValue) => {
        this.product['product']['prs']['prs32'] = '00' + ' / '
          + this.product['product']['prs']['prs31'] + ' / '
          + newValue + ' / '
          + this.product['product']['prs']['prs25'] + ' / '
          + this.product['product']['prs']['prs26'] + ' / '
          + this.product['product']['prs']['prs27'] + ' / ' + '00\n'
        + '10' + ' / ' + this.product['product']['prs']['prs31'] + ' / '
          + newValue + ' / '
          + this.product['product']['prs']['prs25'] + ' / '
          + this.product['product']['prs']['prs26'] + ' / '
          + this.product['product']['prs']['prs27'] + ' / ' + '05'
        this.product['product']['prs']['prs34'] = this.product['product']['prs']['prs31']
        this.updateErrors('prs', 'PRS-32', newValue, this.productStructure[3].params[31])
        this.product['product']['prs']['prs33'] = ''
        Object.entries(this.readingMode).forEach(([key, value]) => {
          this.product['product']['prs']['prs33'] += this.product['product']['prs']['prs31'] + ' / '
          + newValue + ' / '
          + this.product['product']['prs']['prs25'] + ' / '
          + this.product['product']['prs']['prs26'] + ' / '
          + this.product['product']['prs']['prs27'] + ' / '
          + key + ' / ' + value
          if(key !== 'UNKNOWN') {
            this.product['product']['prs']['prs33'] += '\n'
          }
        })
        this.updateErrors('prs', 'PRS-33', newValue, this.productStructure[3].params[32])
        this.product['product']['prs']['prs34'] = this.product['product']['prs']['prs31'] + ' / '
        + newValue + ' / '
        + this.product['product']['prs']['prs25'] + ' / '
        + this.product['product']['prs']['prs26'] + ' / '
        + this.product['product']['prs']['prs27'] + ' / ' + 'R'
        this.updateErrors('prs', 'PRS-34', newValue, this.productStructure[3].params[33])
      }, {
        deep: true
      });
      // If PRS-25 is updated, PRS-32 / 33 / 34 are updated accordingly
      this.$watch('product.product.prs.prs25', (newValue) => {
        this.product['product']['prs']['prs32'] = '00' + ' / '
          + this.product['product']['prs']['prs31'] + ' / '
          + this.product['product']['prs']['prs28'] + ' / '
          + newValue + ' / '
          + this.product['product']['prs']['prs26'] + ' / '
          + this.product['product']['prs']['prs27'] + ' / ' + '00\n'
        + '10' + ' / ' + this.product['product']['prs']['prs31'] + ' / '
          + this.product['product']['prs']['prs28'] + ' / '
          + newValue + ' / '
          + this.product['product']['prs']['prs26'] + ' / '
          + this.product['product']['prs']['prs27'] + ' / ' + '05'
        this.product['product']['prs']['prs34'] = this.product['product']['prs']['prs31']
        this.updateErrors('prs', 'PRS-32', newValue, this.productStructure[3].params[31])
        this.product['product']['prs']['prs33'] = ''
        Object.entries(this.readingMode).forEach(([key, value]) => {
          this.product['product']['prs']['prs33'] += this.product['product']['prs']['prs31'] + ' / '
          + this.product['product']['prs']['prs28'] + ' / '
          + newValue + ' / '
          + this.product['product']['prs']['prs26'] + ' / '
          + this.product['product']['prs']['prs27'] + ' / '
          + key + ' / ' + value
          if(key !== 'UNKNOWN') {
            this.product['product']['prs']['prs33'] += '\n'
          }
        })
        this.updateErrors('prs', 'PRS-33', newValue, this.productStructure[3].params[32])
        this.product['product']['prs']['prs34'] = this.product['product']['prs']['prs31'] + ' / '
        + this.product['product']['prs']['prs28'] + ' / '
        + newValue + ' / '
        + this.product['product']['prs']['prs26'] + ' / '
        + this.product['product']['prs']['prs27'] + ' / ' + 'R'
        this.updateErrors('prs', 'PRS-34', newValue, this.productStructure[3].params[33])
      }, {
        deep: true
      });
      // If PRS-26 is updated, PRS-32 / 33 / 34 are updated accordingly
      this.$watch('product.product.prs.prs26', (newValue) => {
        this.product['product']['prs']['prs32'] = '00' + ' / '
          + this.product['product']['prs']['prs31'] + ' / '
          + this.product['product']['prs']['prs28'] + ' / '
          + this.product['product']['prs']['prs25'] + ' / '
          + newValue + ' / '
          + this.product['product']['prs']['prs27'] + ' / ' + '00\n'
        + '10' + ' / ' + this.product['product']['prs']['prs31'] + ' / '
          + this.product['product']['prs']['prs28'] + ' / '
          + this.product['product']['prs']['prs25'] + ' / '
          + newValue + ' / '
          + this.product['product']['prs']['prs27'] + ' / ' + '05'
        this.product['product']['prs']['prs34'] = this.product['product']['prs']['prs31']
        this.updateErrors('prs', 'PRS-32', newValue, this.productStructure[3].params[31])
        this.product['product']['prs']['prs33'] = ''
        Object.entries(this.readingMode).forEach(([key, value]) => {
          this.product['product']['prs']['prs33'] += this.product['product']['prs']['prs31'] + ' / '
          + this.product['product']['prs']['prs28'] + ' / '
          + this.product['product']['prs']['prs25'] + ' / '
          + newValue + ' / '
          + this.product['product']['prs']['prs27'] + ' / '
          + key + ' / ' + value
          if(key !== 'UNKNOWN') {
            this.product['product']['prs']['prs33'] += '\n'
          }
        })
        this.updateErrors('prs', 'PRS-33', newValue, this.productStructure[3].params[32])
        this.product['product']['prs']['prs34'] = this.product['product']['prs']['prs31'] + ' / '
        + this.product['product']['prs']['prs28'] + ' / '
        + this.product['product']['prs']['prs25'] + ' / '
        + newValue + ' / '
        + this.product['product']['prs']['prs27'] + ' / ' + 'R'
        this.updateErrors('prs', 'PRS-34', newValue, this.productStructure[3].params[33])
      }, {
        deep: true
      });
      // If PRS-27 is updated, PRS-32 / 33 / 34 are updated accordingly
      this.$watch('product.product.prs.prs27', (newValue) => {
        this.product['product']['prs']['prs32'] = '00' + ' / '
          + this.product['product']['prs']['prs31'] + ' / '
          + this.product['product']['prs']['prs28'] + ' / '
          + this.product['product']['prs']['prs25'] + ' / '
          + this.product['product']['prs']['prs26'] + ' / '
          + newValue + ' / ' + '00\n'
        + '10' + ' / ' + this.product['product']['prs']['prs31'] + ' / '
          + this.product['product']['prs']['prs28'] + ' / '
          + this.product['product']['prs']['prs25'] + ' / '
          + this.product['product']['prs']['prs26'] + ' / '
          + newValue + ' / ' + '05'
        this.product['product']['prs']['prs34'] = this.product['product']['prs']['prs31']
        this.updateErrors('prs', 'PRS-32', newValue, this.productStructure[3].params[31])
        this.product['product']['prs']['prs33'] = ''
        Object.entries(this.readingMode).forEach(([key, value]) => {
          this.product['product']['prs']['prs33'] += this.product['product']['prs']['prs31'] + ' / '
          + this.product['product']['prs']['prs28'] + ' / '
          + this.product['product']['prs']['prs25'] + ' / '
          + this.product['product']['prs']['prs26'] + ' / '
          + newValue + ' / '
          + key + ' / ' + value
          if(key !== 'UNKNOWN') {
            this.product['product']['prs']['prs33'] += '\n'
          }
        })
        this.updateErrors('prs', 'PRS-33', newValue, this.productStructure[3].params[32])
        this.product['product']['prs']['prs34'] = this.product['product']['prs']['prs31'] + ' / '
        + this.product['product']['prs']['prs28'] + ' / '
        + this.product['product']['prs']['prs25'] + ' / '
        + this.product['product']['prs']['prs26'] + ' / '
        + newValue + ' / ' + 'R'
        this.updateErrors('prs', 'PRS-34', newValue, this.productStructure[3].params[33])
      }, {
        deep: true
      });
    },

    hasPermission (permission) {
      if (!String.prototype.includes) {
        return this.permissions.indexOf(permission, 0) !== -1
      } else {
        return this.permissions.includes(permission)
      }
    },

    triggerErrorDate () {
      this.dateError = true
      document.getElementById("cardBottom").classList.remove("mb-4")
    },

    updateErrorDate () {
      if (this.updateDate === null || this.updateDate < getPlus1Date()) {
        this.triggerErrorDate();
      }
      else {
        this.dateError = false
        let elt = document.getElementById("cardBottom")
        elt.classList.add("mb-4")
      }
    },
    async getDistributors (user) {
      this.distributors = Object.keys(user.comProductsMap).sort()
    },

    async getDistributorsDetail () {
      if (!this.$store.state.distributors.distributors.length) {
        this.isProcessing = true
        this.error = false

        await this.$store.dispatch('distributors/reset')
        await this.$store.dispatch('distributors/search').then(
          () => {
            this.isProcessing = false
          },
          error => {
            console.log(error)
            this.isProcessing = false
            this.error = true
            this.responseErr = error || 'error.BAD_REQUEST'
          }
        )
      }
    },

    async getStructure () {
      this.isProcessing = true
      this.responseErr = null

      await this.$store.dispatch('products/resetStructure')
      await this.$store.dispatch('products/structure').then(
        () => {
          this.isProcessing = false
          this.productStructure = this.$store.state.products.structure
          this.initProduct()
        },
        error => {
          this.isProcessing = false
          this.responseErr = (error.response.data || 'BAD_REQUEST')
        }
      )
    },

    initProduct () {
      this.product['parent'] = null
      for (var i in this.productStructure) {
        this.product[this.productStructure[i].fieldName] = {}
        this.productErrors[this.productStructure[i].fieldName] = {}
        for (var j in this.productStructure[i].params) {
          this.product[this.productStructure[i].fieldName][this.productStructure[i].params[j].fieldName] = null
          this.productErrors[this.productStructure[i].fieldName][this.productStructure[i].params[j].fieldName] = null
          // Adapt regexs to be used in javascript adn having the same behavior as in Java
          let regexp = this.productStructure[i].params[j].regexp
          regexp = regexp.replaceAll('\\:', ':')
          regexp = regexp.replaceAll('[[ \\t]]', '[ \\t]')
          if(!regexp.includes('^') && !regexp.includes('$')) {
            regexp = '^' + regexp + '$'
            try {
              if(regexp.includes('[\\v')) {
                regexp = regexp.replaceAll('\\v', '\\n\\u000B\\u2028\\u2029')
              }
              else if(regexp.includes('\\v')) {
                regexp = regexp.replaceAll('\\v', '[\\n\\u000B\\u2028\\u2029]')
              }
              else if(regexp.includes('*+')) {
                regexp = regexp.replace('*+', '*')
              }
              this.productStructure[i].params[j].regexp = new RegExp(regexp, 'u')
            } catch (error) {
              console.error("Invalid regex pattern: ", error);
            }
          }
        }
      }
      this.product['prb']['prb4'] = this.updateDate.replace('-', '/').replace('-', '/')

      this.product['status'] = 'ACTIVE'
      let newProduct = {
        "product": this.product,
        "userName": JSON.parse(sessionStorage.getItem('user')).login,
        "requestDate": this.updateDate,
        "requestID": null
      }
      this.product = newProduct
      this.initWatchers()
    },

    onInvalidSubmit ({ results }) {
      for (const result in results) {
        if (Object.prototype.hasOwnProperty.call(results, result)) {
          if (!results[result].valid) {
            let block = result.substring(0,3).toLowerCase()
            let field = result.substring(0,3).toLowerCase() + result.substring(4)
            this.productErrors[block][field] = results[result].errors
            if(!this.blockErrors[result.substring(0,3)]) {
              this.blockErrors[result.substring(0,3)] = true
            }
          }
        }
      }
      if (this.updateDate === null || this.updateDate < getPlus1Date()) {
        this.triggerErrorDate();
      }
      else {
        this.dateError = false
        let elt = document.getElementById("cardBottom")
        elt.classList.add("mb-4")
      }
      this.responseErr = 'INVALID_FORM_PRODUCT'
    },

    updateErrors (block, field, inputValue, param) {
      this.responseErr = null
      let formattedField = field.substring(0,3).toLowerCase() + field.substring(4)
      this.productErrors[block][formattedField] = null;

      if (param.mandatory && !inputValue) {
        this.productErrors[block][formattedField] = getLocalizedMessage(param.code, 'required', this.locale);
        this.blockErrors[block.toUpperCase()] = true
      }

      if (param.regexp && inputValue
      && !new RegExp(param.regexp).test(inputValue)) {
        this.productErrors[block][formattedField] = getLocalizedMessage(param.code, 'regex', this.locale);
        this.blockErrors[block.toUpperCase()] = true
      }

      if(this.updateDate === null || this.updateDate < getPlus1Date()) {
        this.triggerErrorDate();
      }
      else {
        this.dateError = false
        let elt = document.getElementById("cardBottom")
        elt.classList.add("mb-4")
      }

      // Remove error triangle or not
      let allErrorsNull = true;
      for (const field in this.productErrors[block]) {
        if (this.productErrors[block][field] !== null) {
          allErrorsNull = false;
          break;
        }
      }
      if (allErrorsNull) {
        this.blockErrors[block.toUpperCase()] = false;
      }
    },

    setVeeValidateLocale(user) {
      if(user.language === 'FRENCH') {
        setLocale('fr')
        this.locale = 'fr'
      }
      else if (user.language === 'ENGLISH'){
        setLocale('en')
        this.locale = 'en'
      }
      else { // default to french
        setLocale('fr')
        this.locale = 'fr'
      }
    },

    onSubmit () {
      this.create()
    },

    showError (modalError, modalDescription) {
      this.$refs.productErrorDialogRef.showError(modalError, modalDescription)
    },

    showSuccess (successMsg, productCreated) {
      successMsg = this.$i18n.t(successMsg, [productCreated] )
      this.$refs.successDialogRef.showSuccess(successMsg)
    },

    handleExport (product) {
      exportProduct(product, this.productStructure, this.$i18n)
    },

    showModal () {
      return new Promise((resolve) => {
        let pasteModal = new Modal(document.getElementById('pasteModal'))
        pasteModal.show()
        let pasteModelElement = document.getElementById('pasteModal')
        // Set up the event listener for when the modal is closed
        pasteModelElement.addEventListener('hidden.bs.modal', () => {
          resolve()
        })
      })
    },

    async importProduct () {
      let jsonObject = null
      if (!navigator.clipboard) {
        await this.showModal()
        try {
          jsonObject = JSON.parse(this.jsonPastedObject)
        } catch (error) {
          const $toast = useToast()
          $toast.open({
            message: this.$i18n.t('error.INVALID_JSON_PRODUCT'),
            type:'error',
            position: 'bottom',
            duration: 5000,
            dismissible:true
          });
          return;
        }
      }
      else {
        const clipboardContent = await navigator.clipboard.readText()
        try {
          jsonObject = JSON.parse(clipboardContent)
        } catch (error) {
          const $toast = useToast()
          $toast.open({
            message: this.$i18n.t('error.INVALID_JSON_PRODUCT'),
            type:'error',
            position: 'bottom',
            duration: 5000,
            dismissible:true
          });
          return;
        }
      }
      const result = isEqual(jsonObject, this.product['product'])
      if(jsonObject && (result || (jsonObject['prb'] != null && jsonObject['pre'] != null && jsonObject['prc'] != null
        && jsonObject['prs'] != null && jsonObject['pro'] != null && jsonObject['prf'] != null
      ))) {
        this.product['product'] = jsonObject
        this.selectedDistributorRef = this.product['product']['prb']['prb7']
        // Remove parameters not supposed to be present for creation
        for (var i in this.productStructure) {
          let field = this.productStructure[i].fieldName
          for (var j in this.productStructure[i].params) {
            let subField = this.productStructure[i].params[j].fieldName
            if(this.productStructure[i].params[j].outOnly) {
              this.product['product'][field][subField] = null
            }
          }
        }
        const $toast = useToast()
        $toast.open({
          message: this.$i18n.t('label.product.productImported'),
          type:'default',
          position: 'bottom',
          duration: 5000,
          dismissible:true
        });
      }
      else {
        const $toast = useToast()
        $toast.open({
          message: this.$i18n.t('error.INVALID_JSON_PRODUCT'),
          type:'error',
          position: 'bottom',
          duration: 5000,
          dismissible:true
        });
      }
    },

    async create () {
      this.isProcessing = true
      this.responseErr = null
      this.product['product']['prb']['prb7'] = this.selectedDistributorRef

      // put back values to null if they are empty to avoid useless checks ("")
      for(var i in this.product['product']) {
        for(var j in this.product['product'][i]) {
          if(this.product['product'][i][j] === '') {
            this.product['product'][i][j] = null
          }
          else if (this.product['product'][i][j] !== null) {
            if(this.product['product'][i][j].endsWith('\n')  && typeof this.product['product'][i][j] === 'string' || this.product['product'][i][j] instanceof String) {
              this.product['product'][i][j] = this.product['product'][i][j].replace(/\n+$/, '');
            }
          }
        }
      }

      // Add empty blocks for PRL and PRA to avoid errors server-side
      let tmpProductToSend = this.product

      tmpProductToSend['product']['prl'] = {
        'prl1': null,
        'prl2': null,
        'prl3': null,
        'prl4': null,
        'prl5': null,
      }

      tmpProductToSend['product']['pra'] = {
        'pra1': null,
        'pra2': null,
        'pra3': null,
        'pra4': null,
      }

      this.$store.dispatch('products/createProduct', {
        product: tmpProductToSend.product,
        userName: this.product.userName,
        requestDate: this.updateDate,
        requestID: null
      }).then(
        (response) => {
          this.$store.dispatch('products/resetViolations')
          if(response.responseCode != 'SUCCESS') {
            if (response.violations == null) {
              this.showError(response.responseCode, response.responseMessage)
              this.modalDescription = response.responseMessage
            }
            else {
              this.showError(response.responseCode, null)
            }
            // set violations in store to display them in the modal
            this.$store.dispatch('products/setViolations', response.violations)
            this.modalError = response.responseCode || 'BAD_REQUEST'
            this.responseErr = 'INVALID_FORM_PRODUCT'
            this.isProcessing = false
          }
          else {
            this.showSuccess('label.product.creationSuccess', response.productRef)
            this.isProcessing = false
          }
        },
        error => {
          this.isProcessing = false
          this.responseErr = error || 'BAD_REQUEST'
          this.showError(this.responseErr)
        }
      )
    }
  }
}
